import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiSearch, FiLoader, FiPackage, FiBox } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

/**
 * ItemSearchSelect - Component tìm kiếm và chọn nguyên liệu/vật dụng dùng chung
 * 
 * Hỗ trợ 2 chế độ:
 * 1. Static mode: Truyền items qua props
 * 2. API mode: Set useApi=true để fetch từ API
 * 
 * Hỗ trợ tìm kiếm không dấu tiếng Việt
 * 
 * itemType: 'ingredient' | 'supply'
 * 
 * For supplies with options, use supplyOptions prop with format:
 * [{ id, optionName, label, supply }]
 */
const ItemSearchSelect = ({
  // Item type
  itemType = 'ingredient', // 'ingredient' | 'supply'
  
  // Static mode props
  items = [],
  
  // Supply options (flattened list with optionName)
  // Format: [{ id, optionName, label, supply }]
  supplyOptions = null,
  
  // Grouped items (for optgroup display)
  groupedItems = null, // { groupName: items[], ... }
  
  // API mode props
  useApi = false,
  apiParams = {}, // Additional params to pass to API
  
  // Common props
  value = '',
  optionValue = '', // For supply options
  initialItem = null, // Pre-populated item for edit mode
  onChange,
  placeholder,
  label,
  showUnit = true,
  showStock = false,
  showPrice = false,
  clearable = true,
  disabled = false,
  className = '',
  inputClassName = '',
  maxResults = 30,
  emptyText,
  notFoundText,
  loadingText = 'Đang tải...',
  formatPrice,
  filterFn
}) => {
  const [searchText, setSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [apiItems, setApiItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const isTypingRef = useRef(false);
  const lastValueRef = useRef(value);
  const initializedRef = useRef(false);
  const justSelectedRef = useRef(false);

  // Default placeholders and texts based on type
  const defaultPlaceholder = itemType === 'ingredient' 
    ? 'Tìm nguyên liệu...' 
    : 'Tìm vật dụng...';
  const defaultEmptyText = itemType === 'ingredient' 
    ? 'Không có nguyên liệu' 
    : 'Không có vật dụng';
  const defaultNotFoundText = itemType === 'ingredient' 
    ? 'Không tìm thấy nguyên liệu' 
    : 'Không tìm thấy vật dụng';
  
  const apiEndpoint = itemType === 'ingredient' ? '/ingredients' : '/supplies';
  const ItemIcon = itemType === 'ingredient' ? FiPackage : FiBox;
  const accentColor = itemType === 'ingredient' ? 'green' : 'blue';

  // Set initial item on mount
  useEffect(() => {
    if (initialItem && (initialItem.name || initialItem.label)) {
      setSelectedItem(initialItem);
      setSearchText(initialItem.name || initialItem.label || '');
      lastValueRef.current = initialItem._id || initialItem.id;
      initializedRef.current = true;
    }
  }, [initialItem?._id, initialItem?.id, initialItem?.name, initialItem?.label]);

  // Default price formatter
  const priceFormatter = formatPrice || ((price) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(price || 0));
  });

  // Safe get item ID
  const getItemId = (item) => {
    if (!item) return null;
    return item._id || item.id || null;
  };

  // Default filter function with Vietnamese diacritics support
  const filterFunction = filterFn || ((item, text) => {
    if (!item) return false;
    if (!text) return true;
    
    const searchNormalized = removeVietnameseDiacritics(text);
    const nameNormalized = removeVietnameseDiacritics(item.name || item.label || '');
    
    const searchLower = text.toLowerCase();
    const nameLower = (item.name || item.label || '').toLowerCase();
    
    return nameNormalized.includes(searchNormalized) || nameLower.includes(searchLower);
  });

  // Get items based on mode
  const getItems = useCallback(() => {
    // If supply options provided, use those
    if (supplyOptions && supplyOptions.length > 0) {
      return supplyOptions;
    }
    
    if (useApi) {
      return apiItems;
    }
    
    // If grouped items provided, flatten them
    if (groupedItems) {
      const allItems = [];
      Object.entries(groupedItems).forEach(([groupName, groupItems]) => {
        if (Array.isArray(groupItems)) {
          groupItems.forEach(item => {
            allItems.push({ ...item, _groupName: groupName });
          });
        }
      });
      return allItems;
    }
    
    return Array.isArray(items) ? items.filter(i => i && (i.name || i.label)) : [];
  }, [useApi, apiItems, items, groupedItems, supplyOptions]);

  const safeItems = getItems();

  // Fetch items from API
  const fetchItems = useCallback(async (query = '') => {
    if (!useApi) return;
    
    setLoading(true);
    try {
      const params = { limit: maxResults, ...apiParams };
      if (query) {
        params.search = query;
      }
      
      const response = await axios.get(`${API_URL}${apiEndpoint}`, { params });
      setApiItems(response.data.data || response.data || []);
      setHasLoaded(true);
    } catch (error) {
      console.error(`Error fetching ${itemType}s:`, error);
      setApiItems([]);
    } finally {
      setLoading(false);
    }
  }, [useApi, apiEndpoint, maxResults, apiParams, itemType]);

  // Debounced search for API mode
  const debouncedSearch = useCallback((query) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      fetchItems(query);
    }, 300);
  }, [fetchItems]);

  // Update selected item when value prop changes
  useEffect(() => {
    if (isTypingRef.current || value === lastValueRef.current) {
      return;
    }
    
    lastValueRef.current = value;
    
    if (value && safeItems.length > 0) {
      const found = safeItems.find(i => {
        const iid = getItemId(i);
        // For supply options, also check optionName
        if (supplyOptions && optionValue !== undefined) {
          return (iid === value || String(iid) === String(value)) && 
                 (i.optionName === optionValue || (!i.optionName && !optionValue));
        }
        return iid === value || String(iid) === String(value);
      });
      if (found) {
        setSelectedItem(found);
        setSearchText(found.name || found.label || '');
      }
    } else if (!value) {
      setSelectedItem(null);
      setSearchText('');
    }
  }, [value, optionValue, safeItems, supplyOptions]);

  // Sync selected item when items load
  useEffect(() => {
    if (value && safeItems.length > 0 && !selectedItem && !isTypingRef.current) {
      const found = safeItems.find(i => {
        const iid = getItemId(i);
        if (supplyOptions && optionValue !== undefined) {
          return (iid === value || String(iid) === String(value)) && 
                 (i.optionName === optionValue || (!i.optionName && !optionValue));
        }
        return iid === value || String(iid) === String(value);
      });
      if (found) {
        setSelectedItem(found);
        setSearchText(found.name || found.label || '');
      }
    }
  }, [safeItems.length, value, optionValue, selectedItem, supplyOptions]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleFocus = () => {
    // Don't show suggestions if just selected an item
    if (justSelectedRef.current) {
      return;
    }
    setShowSuggestions(true);
    
    if (useApi && !hasLoaded && !loading) {
      fetchItems('');
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      isTypingRef.current = false;
    }, 200);
  };

  const handleSearch = (text) => {
    if (!text) {
      setSelectedItem(null);
      if (onChange) onChange(null);
      
      if (useApi) {
        fetchItems('');
      }
    } else if (useApi) {
      debouncedSearch(text);
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    isTypingRef.current = true;
    justSelectedRef.current = false;
    setSearchText(text);
    setShowSuggestions(true);
    handleSearch(text);
  };

  const handleSelectItem = (item) => {
    isTypingRef.current = false;
    justSelectedRef.current = true;
    lastValueRef.current = getItemId(item);
    initializedRef.current = true;
    setSelectedItem(item);
    setSearchText(item?.name || item?.label || '');
    setShowSuggestions(false);
    // Blur input to prevent dropdown from showing again
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (onChange) onChange(item);
    // Reset justSelected flag after a short delay
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 300);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    isTypingRef.current = false;
    justSelectedRef.current = false;
    lastValueRef.current = null;
    initializedRef.current = false;
    setSelectedItem(null);
    setSearchText('');
    setShowSuggestions(false);
    if (onChange) onChange(null);
  };

  // Filter items
  const filteredItems = useApi 
    ? safeItems 
    : safeItems.filter(i => filterFunction(i, searchText)).slice(0, maxResults);

  // Group filtered items if groupedItems was provided
  const getGroupedFilteredItems = () => {
    if (!groupedItems || useApi || supplyOptions) {
      return null;
    }
    
    const result = {};
    filteredItems.forEach(item => {
      const groupName = item._groupName || 'Khác';
      if (!result[groupName]) {
        result[groupName] = [];
      }
      result[groupName].push(item);
    });
    
    return result;
  };

  const groupedFilteredItems = getGroupedFilteredItems();
  const selectedId = getItemId(selectedItem);

  // Unit labels
  const unitLabels = {
    g: 'g', ml: 'ml', piece: 'quả', unit: 'cái',
    bag: 'túi', box: 'hộp', can: 'lon', bottle: 'chai', kg: 'kg'
  };

  const renderItem = (item, index) => {
    const itemId = getItemId(item);
    const isSelected = selectedItem && itemId && String(selectedId) === String(itemId) && 
      (!supplyOptions || item.optionName === selectedItem.optionName);
    
    const displayName = item.name || item.label;
    
    return (
      <div
        key={`${itemId}-${item.optionName || index}`}
        onClick={() => handleSelectItem(item)}
        className={`px-3 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 flex items-center gap-2 ${
          isSelected ? 'bg-green-50' : ''
        }`}
      >
        <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
          itemType === 'ingredient' ? 'bg-green-100' : 'bg-blue-100'
        }`}>
          <ItemIcon size={14} className={itemType === 'ingredient' ? 'text-green-500' : 'text-blue-500'} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{displayName}</div>
          <div className="text-xs text-gray-500 flex flex-wrap gap-2">
            {showUnit && (item.recipeUnit || item.unit) && (
              <span>Đơn vị: {unitLabels[item.recipeUnit || item.unit] || item.recipeUnit || item.unit}</span>
            )}
            {showStock && item.quantity !== undefined && (
              <span className={item.quantity <= 0 ? 'text-red-500' : ''}>
                Tồn: {item.quantity} {unitLabels[item.unit] || item.unit}
              </span>
            )}
            {showPrice && item.defaultPrice > 0 && (
              <span>{priceFormatter(item.defaultPrice)}đ</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs text-gray-500 mb-1">{label}</label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {loading ? (
            <FiLoader size={16} className="animate-spin" />
          ) : (
            <FiSearch size={16} />
          )}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder || defaultPlaceholder}
          disabled={disabled}
          className={`w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${inputClassName}`}
        />
        
        {clearable && selectedItem && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <FiX size={16} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-[280px] overflow-y-auto z-50">
          {loading ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
              <FiLoader className="animate-spin" />
              {loadingText}
            </div>
          ) : safeItems.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">
              {hasLoaded || !useApi ? (emptyText || defaultEmptyText) : 'Click để tải...'}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">
              {notFoundText || defaultNotFoundText}
            </div>
          ) : groupedFilteredItems ? (
            // Render with groups
            Object.entries(groupedFilteredItems).map(([groupName, groupItems]) => (
              <div key={groupName}>
                <div className="px-3 py-1.5 bg-gray-100 text-xs font-semibold text-gray-600 sticky top-0">
                  {groupName}
                </div>
                {groupItems.map((item, index) => renderItem(item, index))}
              </div>
            ))
          ) : (
            // Render flat list
            filteredItems.map((item, index) => renderItem(item, index))
          )}
        </div>
      )}
    </div>
  );
};

export default ItemSearchSelect;
